import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Mail, Calendar } from 'lucide-react';
import { Button } from '../ui/button';
import { useAuth } from '../../contexts/AuthContext';

const LOGO_URL = "https://static.prod-images.emergentagent.com/jobs/aa1f5b83-186d-4ac3-9dce-2dfcd3ce4c84/images/c740998ea2d13be287d1e30ff6aab30e0baab2ea64c21abe150888905a05c219.jpeg";

const TravelHeader = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { isAuthenticated, isAdmin, logout, user } = useAuth();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Services', path: '/services' },
    { name: 'Consultation', path: '/consultation' },
    { name: 'Travel Resources', path: '/resources' },
    { name: 'About Brandi', path: '/about' },
    { name: 'Contact', path: '/contact' },
  ];

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md py-2' : 'bg-transparent py-4'
      }`}
    >
      {/* Top bar - visible on desktop */}
      <div className={`hidden lg:block border-b transition-colors ${isScrolled ? 'border-gray-100' : 'border-white/10'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-2 text-sm">
            <div className="flex items-center gap-6">
              <a 
                href="mailto:brandi@livelifeglobalwellness.com" 
                className={`flex items-center gap-2 transition-colors ${isScrolled ? 'text-text-secondary hover:text-gold' : 'text-white/80 hover:text-white'}`}
              >
                <Mail className="w-4 h-4" />
                brandi@livelifeglobalwellness.com
              </a>
            </div>
            <div className={`flex items-center gap-2 ${isScrolled ? 'text-text-secondary' : 'text-white/80'}`}>
              <span>Houston, TX</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main navigation */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3">
            <img 
              src={LOGO_URL} 
              alt="Live Life Global Wellness Advisory Group" 
              className="h-12 md:h-14 w-auto"
            />
            <div className={`hidden sm:block ${isScrolled ? 'text-navy' : 'text-white'}`}>
              <span className="font-serif text-lg md:text-xl font-semibold block leading-tight">Live Life Global Wellness Advisory Group</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  isActive(link.path)
                    ? isScrolled 
                      ? 'text-gold bg-gold/10' 
                      : 'text-gold bg-white/10'
                    : isScrolled 
                      ? 'text-text-secondary hover:text-gold hover:bg-gold/5' 
                      : 'text-white/90 hover:text-white hover:bg-white/10'
                }`}
              >
                {link.name}
              </Link>
            ))}
          </nav>

          {/* CTA Button & Auth */}
          <div className="hidden lg:flex items-center gap-4">
            {isAuthenticated ? (
              <>
                {isAdmin && (
                  <Link to="/admin">
                    <Button 
                      variant="outline" 
                      className={`rounded-full ${isScrolled ? 'border-navy text-navy hover:bg-navy hover:text-white' : 'border-white text-white hover:bg-white hover:text-navy'}`}
                    >
                      Admin
                    </Button>
                  </Link>
                )}
                <Button
                  onClick={logout}
                  variant="ghost"
                  className={`rounded-full ${isScrolled ? 'text-text-secondary hover:text-navy' : 'text-white/80 hover:text-white'}`}
                >
                  Logout
                </Button>
              </>
            ) : (
              <Link to="/login">
                <Button
                  variant="ghost"
                  className={`rounded-full ${isScrolled ? 'text-text-secondary hover:text-navy' : 'text-white/80 hover:text-white'}`}
                >
                  Login
                </Button>
              </Link>
            )}
            <Link to="/consultation">
              <Button className="bg-gold hover:bg-gold-dark text-navy font-semibold px-6 py-2 rounded-full">
                <Calendar className="w-4 h-4 mr-2" />
                Book Now
              </Button>
            </Link>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`lg:hidden p-2 rounded-lg ${isScrolled ? 'text-navy' : 'text-white'}`}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-white border-t shadow-lg">
          <div className="px-4 py-6 space-y-4">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setMobileMenuOpen(false)}
                className={`block px-4 py-3 rounded-xl text-base font-medium transition-colors ${
                  isActive(link.path)
                    ? 'text-gold bg-gold/10'
                    : 'text-text-secondary hover:text-gold hover:bg-gold/5'
                }`}
              >
                {link.name}
              </Link>
            ))}
            
            <div className="pt-4 border-t space-y-3">
              {isAuthenticated ? (
                <>
                  {isAdmin && (
                    <Link to="/admin" onClick={() => setMobileMenuOpen(false)}>
                      <Button variant="outline" className="w-full rounded-full border-navy text-navy">
                        Admin Dashboard
                      </Button>
                    </Link>
                  )}
                  <Button
                    onClick={() => { logout(); setMobileMenuOpen(false); }}
                    variant="ghost"
                    className="w-full rounded-full text-text-secondary"
                  >
                    Logout
                  </Button>
                </>
              ) : (
                <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                  <Button variant="ghost" className="w-full rounded-full text-text-secondary">
                    Login
                  </Button>
                </Link>
              )}
              <Link to="/consultation" onClick={() => setMobileMenuOpen(false)}>
                <Button className="w-full bg-gold hover:bg-gold-dark text-navy font-semibold rounded-full">
                  <Calendar className="w-4 h-4 mr-2" />
                  Book Consultation - $25
                </Button>
              </Link>
            </div>
            
            <div className="pt-4 border-t text-center">
              <a href="mailto:brandi@livelifeglobalwellness.com" className="text-sm text-text-secondary flex items-center justify-center gap-2">
                <Mail className="w-4 h-4" />
                brandi@livelifeglobalwellness.com
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default TravelHeader;
